/**
 * Permissions Service
 *
 * Conecta el frontend con el modelo granular de roles/permisos de Supabase
 * (tablas `company_users` y `permissions`, funciones RPC).
 *
 * Lógica compartida web + mobile (importa vía alias @shared).
 */

import { supabase } from '@shared/libs/supabaseClient';
import type {
  Action,
  CompanyUser,
  CompanyUserWithProfile,
  Role,
  UserCompany,
  UserPermissions,
} from '@shared/types/permissions';

// Cache en memoria de permisos por (userId, empresaId) para evitar RPC por cada
// render. Se invalida al cambiar de empresa o rol.
const permissionsCache = new Map<string, UserPermissions>();

const cacheKey = (userId: string, empresaId: string) => `${userId}:${empresaId}`;

// Prefijo de la persistencia offline de membresía (rol + matriz de permisos
// confirmada por el server). Ver saveLastKnownMembership.
const MEMBERSHIP_STORAGE_PREFIX = 'mcd:membership:';

const membershipStorageKey = (userId: string, empresaId: string) =>
  `${MEMBERSHIP_STORAGE_PREFIX}${userId}:${empresaId}`;

/** Última membresía conocida (confirmada por el server y persistida). */
export interface LastKnownMembership {
  role: Role;
  permissions: UserPermissions;
  /** ISO timestamp de la última confirmación exitosa con el server. */
  savedAt: string;
}

// localStorage no existe en React Native (este service se comparte vía @shared):
// sin él, la persistencia offline es no-op y mobile usa solo el cache en memoria.
const hasLocalStorage = typeof localStorage !== 'undefined';

export const permissionsService = {
  /**
   * Obtiene la matriz de permisos del usuario en una empresa.
   * Usa la función RPC `get_user_permissions` (SECURITY DEFINER) de Supabase.
   * Lanza en error de red/RPC (mismo contrato que getUserCompanies) para que
   * AuthContext distinga "error transitorio" de "sin permisos" — devolver {}
   * hacía indistinguible un fallo de red de una matriz realmente vacía.
   */
  async getUserPermissions(userId: string, empresaId: string): Promise<UserPermissions> {
    const key = cacheKey(userId, empresaId);
    const cached = permissionsCache.get(key);
    if (cached) return cached;

    const { data, error } = await supabase.rpc('get_user_permissions', {
      p_user_id: userId,
      p_empresa_id: empresaId,
    });

    if (error) {
      console.error('[permissionsService] Error loading permissions:', error.message);
      throw error;
    }

    const permissions: UserPermissions = {};
    for (const row of data || []) {
      permissions[row.module as keyof UserPermissions] = {
        canView: !!row.can_view,
        canCreate: !!row.can_create,
        canEdit: !!row.can_edit,
        canDelete: !!row.can_delete,
      };
    }

    permissionsCache.set(key, permissions);
    return permissions;
  },

  /** Invalida el cache (al cambiar rol/empresa). */
  clearCache(userId?: string, empresaId?: string) {
    if (userId && empresaId) {
      permissionsCache.delete(cacheKey(userId, empresaId));
    } else {
      permissionsCache.clear();
    }
  },

  /**
   * Persiste la última membresía conocida (rol + permisos) confirmada por el
   * server. La usa AuthContext como fallback offline: sin esto, un arranque en
   * frío sin conexión dejaba la matriz vacía y PermissionGuard redirigía a
   * /acceso-denegado. La RLS de Supabase sigue siendo la barrera real.
   */
  saveLastKnownMembership(userId: string, empresaId: string, role: Role, permissions: UserPermissions) {
    if (!hasLocalStorage) return;
    try {
      const payload: LastKnownMembership = {
        role,
        permissions,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem(membershipStorageKey(userId, empresaId), JSON.stringify(payload));
    } catch (e) {
      console.warn('[permissionsService] No se pudo persistir membresía:', e);
    }
  },

  /**
   * Última membresía persistida para (userId, empresaId), o null si nunca se
   * confirmó una con el server en este dispositivo.
   */
  getLastKnownMembership(userId: string, empresaId: string): LastKnownMembership | null {
    if (!hasLocalStorage) return null;
    try {
      const raw = localStorage.getItem(membershipStorageKey(userId, empresaId));
      if (!raw) return null;
      const parsed = JSON.parse(raw) as LastKnownMembership;
      if (!parsed?.role || !parsed?.permissions) return null;
      return parsed;
    } catch (e) {
      console.warn('[permissionsService] Membresía persistida ilegible:', e);
      return null;
    }
  },

  /**
   * Limpia la membresía persistida. Sin argumentos, limpia todas las del
   * dispositivo (se usa en sign-out y suspensión total).
   */
  clearLastKnownMembership(userId?: string, empresaId?: string) {
    if (!hasLocalStorage) return;
    try {
      if (userId && empresaId) {
        localStorage.removeItem(membershipStorageKey(userId, empresaId));
        return;
      }
      const keysToRemove: string[] = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key?.startsWith(MEMBERSHIP_STORAGE_PREFIX)) keysToRemove.push(key);
      }
      keysToRemove.forEach((key) => localStorage.removeItem(key));
    } catch (e) {
      console.warn('[permissionsService] No se pudo limpiar membresía persistida:', e);
    }
  },

  /** Verifica un permiso puntual contra una matriz ya cargada. */
  can(permissions: UserPermissions, module: keyof UserPermissions, action: Action): boolean {
    const mod = permissions[module];
    if (!mod) return false;
    switch (action) {
      case 'view':
        return mod.canView;
      case 'create':
        return mod.canCreate;
      case 'edit':
        return mod.canEdit;
      case 'delete':
        return mod.canDelete;
      default:
        return false;
    }
  },

  /**
   * Rol del usuario en una empresa (desde company_users).
   * Devuelve null si no tiene membership activa.
   * Lanza en error de red/RPC (mismo contrato que getUserCompanies) para que
   * AuthContext distinga "error transitorio" de "sin membership" — devolver
   * null hacía indistinguible un fallo de red de una suspensión real.
   */
  async getUserRole(userId: string, empresaId: string): Promise<Role | null> {
    const { data, error } = await supabase
      .from('company_users')
      .select('role')
      .eq('user_id', userId)
      .eq('empresa_id', empresaId)
      .eq('status', 'active')
      .maybeSingle();

    if (error) {
      console.error('[permissionsService] Error loading role:', error.message);
      throw error;
    }
    return (data?.role as Role) ?? null;
  },

  /**
   * Empresas a las que pertenece el usuario (para el selector multi-empresa).
   * Lanza en error de red (a diferencia de otras, que devuelven []) para que
   * AuthContext pueda distinguir "offline" de "vacío real" (suspendido de
   * todas) y no trate a un usuario offline como si no tuviera empresa.
   */
  async getUserCompanies(userId: string): Promise<UserCompany[]> {
    const { data, error } = await supabase
      .from('company_users')
      .select('empresa_id, role, status, empresas:empresas(id, nombre)')
      .eq('user_id', userId)
      .eq('status', 'active')
      .order('invited_at', { ascending: true });

    if (error) {
      console.error('[permissionsService] Error loading companies:', error.message);
      throw error;
    }

    return (data || []).map((row: any) => ({
      empresa_id: row.empresa_id,
      empresa_nombre: row.empresas?.nombre ?? 'Sin nombre',
      role: row.role as Role,
      status: row.status,
    }));
  },

  /** Lista de miembros de una empresa (para gestión de usuarios). */
  async getCompanyUsers(empresaId: string): Promise<CompanyUserWithProfile[]> {
    const { data, error } = await supabase
      .from('company_users')
      .select('id, empresa_id, user_id, role, status, invited_by, invited_at, accepted_at, created_at, updated_at')
      .eq('empresa_id', empresaId)
      .order('created_at', { ascending: true });

    if (error) {
      console.error('[permissionsService] Error loading company users:', error.message);
      return [];
    }

    // Enriquecer con datos de perfil vía la vista users_with_permissions
    // (joinea profiles; la vista filtra status='active' y respeta RLS).
    const members = (data || []) as CompanyUser[];
    if (members.length === 0) return [];

    const userIds = members.map((m) => m.user_id);
    const { data: withProfile } = await supabase
      .from('users_with_permissions')
      .select('user_id, email, nombre, apellido')
      .in('user_id', userIds);

    const profileMap = new Map<string, { email?: string; nombre?: string | null; apellido?: string | null }>();
    for (const row of withProfile || []) {
      profileMap.set(row.user_id, { email: row.email, nombre: row.nombre, apellido: row.apellido });
    }

    return members.map((m) => ({
      ...m,
      ...(profileMap.get(m.user_id) ?? {}),
    }));
  },

  /** Cambia el rol de un miembro. El RLS de company_users valida permisos. */
  async updateUserRole(empresaId: string, userId: string, role: Role): Promise<void> {
    const { error } = await supabase
      .from('company_users')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('empresa_id', empresaId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  /** Cambia el estado de un miembro (active/inactive/suspended). */
  async updateUserStatus(
    empresaId: string,
    userId: string,
    status: 'active' | 'inactive' | 'suspended',
  ): Promise<void> {
    const { error } = await supabase
      .from('company_users')
      .update({ status, updated_at: new Date().toISOString() })
      .eq('empresa_id', empresaId)
      .eq('user_id', userId);

    if (error) throw error;
  },

  /** Elimina a un miembro de la empresa. */
  async removeUser(empresaId: string, userId: string): Promise<void> {
    const { error } = await supabase
      .from('company_users')
      .delete()
      .eq('empresa_id', empresaId)
      .eq('user_id', userId);

    if (error) throw error;
  },
};

export type { CompanyUser };
